import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FieldErrors } from 'react-hook-form';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  NotificationRule,
  notificationRuleBaseSchema,
} from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { NotificationRuleMessageTemplateForm } from '../shared/notification-rule-message-template-form.tsx';
import { EditNotificationRuleConditionsForm } from './edit-notification-rule-conditions-form';
import { LoaderIcon } from 'lucide-react';
import { usePatchNotificationRuleMutation } from '@/features/notification-rules/hooks/mutations/use-patch-notification-rule.ts';

type EditTab = 'general' | 'message' | 'conditions';

const FIELD_TO_TAB: Record<string, EditTab> = {
  status: 'general',
  cooldownPeriod: 'general',
  notificationTemplate: 'message',
  notificationPreviewTemplate: 'message',
  conditions: 'conditions',
};

interface NotificationRuleEditFormProps {
  notificationRule: NotificationRule;
  onClose?: () => void;
}

export function NotificationRuleEditForm({
  notificationRule,
  onClose,
}: NotificationRuleEditFormProps) {
  const [activeTab, setActiveTab] = useState<EditTab>('general');

  const form = useForm({
    resolver: zodResolver(notificationRuleBaseSchema),
    defaultValues: {
      ...notificationRule,
    },
    mode: 'onChange',
  });

  const { mutate, isPending } = usePatchNotificationRuleMutation({
    onSuccess: () => {
      onClose?.();
    },
  });

  const onSubmit = (data: NotificationRule) => {
    mutate({
      notificationRuleId: data.notificationRuleId,
      cooldownPeriod: Number(data.cooldownPeriod),
      status: data.status,
      notificationTemplate: data.notificationTemplate,
      notificationPreviewTemplate: data.notificationPreviewTemplate,
    });
  };

  const onError = (errors: FieldErrors<NotificationRule>) => {
    for (const fieldName of Object.keys(errors)) {
      const tab = FIELD_TO_TAB[fieldName];
      if (tab) {
        setActiveTab(tab);
        return;
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, onError)} className="space-y-4">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as EditTab)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="message">Message</TabsTrigger>
            <TabsTrigger value="conditions">Conditions</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-2">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Status</FormLabel>
                    <FormDescription>
                      Enable or disable this notification rule.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value === 'enabled'}
                      onCheckedChange={(checked) =>
                        field.onChange(checked ? 'enabled' : 'disabled')
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cooldownPeriod"
              render={({ field }) => (
                <FormItem className="mb-2">
                  <FormLabel>Cooldown Period (minutes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="e.g., 60"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <FormDescription>
                    Time before the same alert can be triggered again (60-10080 minutes).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

          <TabsContent value="message" className="mt-2">
            <NotificationRuleMessageTemplateForm showTitle={false} />
          </TabsContent>

          <TabsContent value="conditions" className="mt-2">
            <EditNotificationRuleConditionsForm showTitle={false} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2">
          {Boolean(onClose) && (
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isPending}>
            {isPending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  );
}
